// packages/web-ui/src/api/clients/settings.ts
//
// W6-S2 — Typed client for the merged-settings + batch-write surface.
//
// Endpoints (server: packages/server/src/routes/settings.ts):
//   GET /api/v1/settings                       → MergedSettings
//   PUT /api/v1/settings  (single)             → SettingsPutResponse
//   PUT /api/v1/settings  (batch)              → SettingsBatchPutResponse
//
// Endpoints NOT YET shipped by W6-S1 (Reveal + Migrate). The functions exist
// in this client so consumers can wire them, but they short-circuit to a
// `Result.err({ code: 'ENDPOINT_NOT_AVAILABLE' })` until the server lands
// them. Per the story: this story renders the [Reveal] button as `disabled`
// when the endpoint is missing, and the deprecation `[Migrate now]`/`[Keep
// both]` buttons fall back to an explicit no-op + console warn.
//
// Returns `Result<T, ApiError>` per CLAUDE.md "no throws across boundaries".

import type { Result } from '@tessa-lean/base';
import type {
  MergedSettings,
  SettingsBatchPutRequest,
  SettingsBatchPutResponse,
  SettingsScope,
} from '@tessa-lean/base/api-schemas/index.js';
import { apiCall, type ApiContext, type ApiError } from '../client.js';

export interface MigrateKeyRequest {
  legacyKey: string;
  newKey: string;
  scope: SettingsScope;
  /** True for [Migrate now] (delete legacy); false for [Keep both]. */
  deleteOriginal: boolean;
}

export interface MigrateKeyResponse {
  written: boolean;
  legacyDeleted: boolean;
}

export interface RevealSecretResponse {
  value: string;
}

/** GET /api/v1/settings — full merged-settings snapshot. */
export async function getMergedSettings(
  ctx: ApiContext,
): Promise<Result<MergedSettings, ApiError>> {
  return apiCall<undefined, MergedSettings>(ctx, 'GET', `/api/v1/settings`);
}

/** PUT /api/v1/settings — batch update. `value: null` deletes the key. */
export async function batchUpdate(
  ctx: ApiContext,
  body: SettingsBatchPutRequest,
): Promise<Result<SettingsBatchPutResponse, ApiError>> {
  return apiCall<SettingsBatchPutRequest, SettingsBatchPutResponse>(
    ctx,
    'PUT',
    `/api/v1/settings`,
    body,
  );
}

/**
 * POST /api/v1/settings/migrate-key — copies a `sys-jarvis.*` value to the
 * matching `tessa.*` key under the requested scope; optionally deletes the
 * legacy entry. Endpoint NOT YET shipped by W6-S1; we return a typed
 * `ENDPOINT_NOT_AVAILABLE` so the SPA can render a friendly disabled state.
 */
export async function migrateKey(
  ctx: ApiContext,
  body: MigrateKeyRequest,
): Promise<Result<MigrateKeyResponse, ApiError>> {
  const result = await apiCall<MigrateKeyRequest, MigrateKeyResponse>(
    ctx,
    'POST',
    `/api/v1/settings/migrate-key`,
    body,
  );
  // Translate a 404 from the server into our typed marker so the UI can
  // distinguish "endpoint missing" from "request was malformed".
  if (!result.ok && (result.error.code === 'HTTP_ERROR' || result.error.code === 'NOT_FOUND')) {
    return {
      ok: false,
      error: {
        code: 'ENDPOINT_NOT_AVAILABLE',
        message: 'Migrate-key endpoint is not yet shipped by the server.',
        retryable: false,
        requestId: result.error.requestId,
      },
    };
  }
  return result;
}

/**
 * GET /api/v1/settings/{key}/reveal — server hands back the plaintext for a
 * secret key on user-action. Same `ENDPOINT_NOT_AVAILABLE` gate as
 * `migrateKey`. The revealed value is held only in component-local state and
 * cleared after 10 s; never enters the zustand store.
 */
export async function revealSecret(
  ctx: ApiContext,
  qualifiedKey: string,
): Promise<Result<RevealSecretResponse, ApiError>> {
  const result = await apiCall<undefined, RevealSecretResponse>(
    ctx,
    'GET',
    `/api/v1/settings/${encodeURIComponent(qualifiedKey)}/reveal`,
  );
  if (!result.ok && (result.error.code === 'HTTP_ERROR' || result.error.code === 'NOT_FOUND')) {
    return {
      ok: false,
      error: {
        code: 'ENDPOINT_NOT_AVAILABLE',
        message: 'Reveal endpoint is not yet shipped by the server.',
        retryable: false,
        requestId: result.error.requestId,
      },
    };
  }
  return result;
}

export const settingsApi = {
  getMergedSettings,
  batchUpdate,
  migrateKey,
  revealSecret,
};

export type SettingsApi = typeof settingsApi;
